import { Directive, ElementRef, Renderer2, inject } from '@angular/core';

import { WINDOW_TOKEN } from '../../../services/window.token';
import { MenuService } from '../../../services/menu/menu.service';
import { StickyState } from '../../../domain/menu.domain';
import { StickyMenuContainerDirective } from './sticky-menu-container.directive';

/**
 * Sticky Menu Directive
 *
 * This directive controls whether to display full menu or sticky menu
 */
@Directive({
  selector: '[tdWbStickyMenu]',
  host: {
    '(window:scroll)': 'onWindowScroll()'
  }
})
export class StickyMenuDirective {
  private readonly menuService = inject(MenuService);
  private readonly renderer = inject(Renderer2);
  private readonly hostElement = inject(ElementRef);
  private readonly stickyContainer = inject(StickyMenuContainerDirective);
  private readonly browserWindow = inject<Window>(WINDOW_TOKEN);

  /**
   * Page Y Offset
   */
  private static readonly SCROLL_OFFSET_UP: number = 42;

  /**
   * Current sticky state
   */
  private currentState: StickyState = StickyState.FULL;

  /**
   * Class by sticky state
   */
  private stickyClass = new Map<string, string>();

  /**
   * The constructor of the component.
   *
   * @param mediaQueryService Menu Query Service
   * @param menuService Menu Service
   * @param renderer Renderer
   * @param hostElement Element Reference
   * @param stickyContainer Sticky Container
   * @param browserWindow Window Object
   */
  constructor() {
    this.stickyClass.set('full', '');
    this.stickyClass.set('stickyHeader', 'td-wb-header--sticky');
  }

  /**
   * Scroll Event Listener
   */onWindowScroll(): void {
    this.scrollHandler();
  }

  /**
   * This function updates sticky classes to the host element and sticky container element.
   */
  private scrollHandler(): void {
    const menuStateChanged = this.updateState();
    if (menuStateChanged) {
      this.renderer.removeClass(
        this.hostElement.nativeElement,
        'td-wb-header--sticky'
      );
      if (this.currentState !== StickyState.FULL) {
        this.renderer.addClass(this.hostElement.nativeElement, this.currentState);
      }
      this.stickyContainer.toggleClass(this.currentState === StickyState.STICKY);
      this.menuService.onStickyStateChange(this.currentState);
    }
  }

  /**
   * This function updates sticky state.
   *
   * @returns Returns true if state has been updated. Otherwise, false.
   */
  private updateState(): boolean {
    const newState = this.getMenuState();
    if (this.currentState !== newState) {
      this.currentState = newState;
      return true;
    }
    return false;
  }

  /**
   * This function gets menu state based on page offset.
   *
   * @returns Returns menu state
   */
  private getMenuState(): StickyState {
    return this.browserWindow.pageYOffset < StickyMenuDirective.SCROLL_OFFSET_UP
      ? StickyState.FULL
      : StickyState.STICKY;
  }
}
